import { SPECIAL_CONTENT_TYPES } from '../../constants.ts';
import { TableCellContentType } from '../../types.ts';
import type { ColumnWidth } from '../Head/types.ts';

const DEFAULT_COLUMN = 'minmax(min-content, 1fr)';
// The `'fluid'` preset keeps a readable floor so it can't collapse to unreadable narrowness when sibling
// columns are greedy; once it would shrink past this floor the table scrolls horizontally instead. For
// full shrink (down to 0), use the object form `{ min: 0, grow: true }`.
const FLUID_MIN_WIDTH = '160px';

export interface ColumnInput {
  width?: ColumnWidth;
  contentType?: TableCellContentType;
}

export interface ColumnMeta {
  columns: string;
  hasDnd: boolean;
  hasCheckbox: boolean;
  hasAction: boolean;
  // True when a column is meant to fill and shrink (a `'fluid'` preset or `{ grow: true }`). The grid is
  // then width-constrained to the viewport (not `max-content`) so the track actually shrinks and wraps.
  fluid: boolean;
}

const toLength = (value: number | string): string => (typeof value === 'number' ? `${value}px` : value);

// Compiles a single `<Table.Head width>` value into exactly one grid-template-columns track.
// One track per head keeps countColumns (and the Loader/SubRow spans that rely on it) correct.
export function widthToTrack(width: ColumnWidth | undefined, contentType?: TableCellContentType): string {
  // Special cells (drag handle, checkbox, action) are intrinsically fixed-width and auto-sized, so they
  // always size to content regardless of any `width` — a wider track would only reserve unused space.
  if (contentType && SPECIAL_CONTENT_TYPES.has(contentType)) return 'auto';
  if (width === undefined) return DEFAULT_COLUMN;
  if (typeof width === 'number') return `${width}px`;
  if (typeof width === 'string') {
    switch (width) {
      case 'fluid':
        return `minmax(${FLUID_MIN_WIDTH}, 1fr)`;
      case 'auto':
        return 'auto';
      case 'content':
        return 'max-content';
      case 'min':
        return 'min-content';
    }
  }
  const grow = width.grow ?? false;
  const min = width.min ?? (grow ? '0' : 'min-content');
  const max = width.max ?? (grow ? '1fr' : 'max-content');
  return `minmax(${toLength(min)}, ${toLength(max)})`;
}

// Builds the grid-template-columns string and the special-cell flags from the head columns.
// The action cell pins itself to the right edge of its trailing auto track via `margin-left: auto`
// (see actionCellStyles), so no column-distribution is needed here to keep the action flush right.
export function buildGridColumns(columns: ColumnInput[]): ColumnMeta {
  const tracks: string[] = [];
  let hasDnd = false;
  let hasCheckbox = false;
  let hasAction = false;
  let fluid = false;

  for (const { width, contentType } of columns) {
    const isSpecial = contentType !== undefined && SPECIAL_CONTENT_TYPES.has(contentType);
    tracks.push(widthToTrack(width, contentType));
    // Special cells ignore `width` (see widthToTrack), so they can't contribute a fluid/grow track.
    if (!isSpecial && (width === 'fluid' || (typeof width === 'object' && width.grow === true))) fluid = true;
    if (contentType === TableCellContentType.DND) hasDnd = true;
    if (contentType === TableCellContentType.CHECKBOX) hasCheckbox = true;
    if (contentType === TableCellContentType.ACTION) hasAction = true;
  }

  return { columns: tracks.join(' '), hasDnd, hasCheckbox, hasAction, fluid };
}
